// ═══════════════════════════════════════════════════════════════════
// ADMIN ANALYTICS UNIFIED — Single Source of Truth
// ═══════════════════════════════════════════════════════════════════
// Covers: Audience, Community, Revenue, Performance
// This file replaces: adminAnalytics.ts, communityAnalytics.ts,
//   monetizationAnalytics.ts (KPIs only), adminMonetizationUnified.ts (KPIs only)
// The detailed monetization data (ownAds, rfwCampaigns, sponsors, etc.)
// still lives in adminMonetizationUnified.ts and is imported here.

import {
  ownAdsRevenue,
  rfwRadioRevenue,
  sponsorshipsRevenue,
  marketplaceRevenue,
  totalRadioRevenue,
  totalGrossRevenue,
  totalImpressions as unifiedTotalImpressions,
  totalClicks as unifiedTotalClicks,
  avgCtr as unifiedAvgCtr,
  revenueKpis as unifiedRevenueKpis,
  performanceKpis as unifiedPerformanceKpis,
  topsData as unifiedTopsData,
  monetizationTimePeriodLabels,
  monthLabel,
} from './adminMonetizationUnified';

/* ═══════════════════════════════════════════════════════════
   SHARED TYPES
   ═══════════════════════════════════════════════════════════ */

export type TimePeriod = 'hoy' | 'semana' | 'mes' | 'ano';

export interface PeriodData {
  value: number;
  trend: 'up' | 'down' | 'flat';
  trendLabel: string;
}

export interface AnalyticsKpi {
  id: string;
  label: string;
  icon: string;
  color: string;
  unit: string;
  hoy: PeriodData;
  semana: PeriodData;
  mes: PeriodData;
  ano: PeriodData;
  /** Category grouping: audience | community | revenue | performance */
  group: 'audience' | 'community' | 'revenue' | 'performance';
}

export interface TopItem {
  rank: number;
  name: string;
  value: number;
  unit: string;
  trend: 'up' | 'down' | 'flat';
  subtitle?: string;
  extraLabel?: string;
  extraValue?: string;
}

export interface UnifiedTops {
  // ── Audience tops (from adminAnalytics.ts) ──
  programas: TopItem[];
  locutores: TopItem[];
  eventos: TopItem[];
  concursos: TopItem[];
  // ── Community tops (from communityAnalytics.ts) ──
  miembros: TopItem[];
  comunidadProgramas: TopItem[];
  horarios: TopItem[];
  // ── Revenue tops (from adminMonetizationUnified.ts) ──
  campanas: TopItem[];
  patrocinadores: TopItem[];
  marketplace: TopItem[];
}

/* ═══════════════════════════════════════════════════════════
   TIME PERIOD LABELS
   ═══════════════════════════════════════════════════════════ */

export const timePeriodLabels: Record<TimePeriod, string> = {
  hoy: 'Hoy',
  semana: 'Semana',
  mes: 'Mes',
  ano: 'Año',
};

/* ═══════════════════════════════════════════════════════════
   1. AUDIENCE KPIs (from adminAnalytics.ts — kept as-is)
   ═══════════════════════════════════════════════════════════ */

export const audienceKpis: AnalyticsKpi[] = [
  {
    id: 'oyentes',
    label: 'Oyentes',
    icon: 'ri-headphone-line',
    color: 'emerald',
    unit: '',
    group: 'audience',
    hoy: { value: 1542, trend: 'up', trendLabel: '+11.7%' },
    semana: { value: 12847, trend: 'up', trendLabel: '+14.7%' },
    mes: { value: 52100, trend: 'up', trendLabel: '+6.5%' },
    ano: { value: 534500, trend: 'up', trendLabel: '+18.2%' },
  },
  {
    id: 'seguidores',
    label: 'Seguidores',
    icon: 'ri-user-heart-line',
    color: 'rose',
    unit: '',
    group: 'audience',
    hoy: { value: 28, trend: 'up', trendLabel: '+16.7%' },
    semana: { value: 342, trend: 'down', trendLabel: '-5.2%' },
    mes: { value: 1480, trend: 'up', trendLabel: '+22.8%' },
    ano: { value: 12450, trend: 'up', trendLabel: '+34.1%' },
  },
  {
    id: 'programas',
    label: 'Programas',
    icon: 'ri-music-line',
    color: 'sky',
    unit: '',
    group: 'audience',
    hoy: { value: 8, trend: 'flat', trendLabel: '0.0%' },
    semana: { value: 42, trend: 'up', trendLabel: '+5.0%' },
    mes: { value: 186, trend: 'up', trendLabel: '+8.1%' },
    ano: { value: 2180, trend: 'up', trendLabel: '+12.4%' },
  },
  {
    id: 'locutores',
    label: 'Locutores',
    icon: 'ri-mic-line',
    color: 'violet',
    unit: '',
    group: 'audience',
    hoy: { value: 5, trend: 'flat', trendLabel: '0.0%' },
    semana: { value: 12, trend: 'flat', trendLabel: '0.0%' },
    mes: { value: 14, trend: 'up', trendLabel: '+7.7%' },
    ano: { value: 18, trend: 'up', trendLabel: '+28.6%' },
  },
  {
    id: 'eventos',
    label: 'Eventos',
    icon: 'ri-calendar-event-line',
    color: 'orange',
    unit: '',
    group: 'audience',
    hoy: { value: 3, trend: 'up', trendLabel: '+50.0%' },
    semana: { value: 12, trend: 'up', trendLabel: '+33.3%' },
    mes: { value: 28, trend: 'up', trendLabel: '+27.3%' },
    ano: { value: 215, trend: 'up', trendLabel: '+41.4%' },
  },
  {
    id: 'concursos',
    label: 'Concursos',
    icon: 'ri-trophy-line',
    color: 'amber',
    unit: '',
    group: 'audience',
    hoy: { value: 2, trend: 'flat', trendLabel: '0.0%' },
    semana: { value: 8, trend: 'up', trendLabel: '+14.3%' },
    mes: { value: 34, trend: 'up', trendLabel: '+21.4%' },
    ano: { value: 178, trend: 'up', trendLabel: '+15.6%' },
  },
];

/* ═══════════════════════════════════════════════════════════
   2. COMMUNITY KPIs (from communityAnalytics.ts — kept as-is)
   ═══════════════════════════════════════════════════════════ */

export const communityKpis: AnalyticsKpi[] = [
  {
    id: 'saludos',
    label: 'Saludos',
    icon: 'ri-hand-heart-line',
    color: 'rose',
    unit: '',
    group: 'community',
    hoy: { value: 87, trend: 'up', trendLabel: '+14.5%' },
    semana: { value: 623, trend: 'up', trendLabel: '+9.2%' },
    mes: { value: 2410, trend: 'up', trendLabel: '+18.3%' },
    ano: { value: 28450, trend: 'up', trendLabel: '+31.7%' },
  },
  {
    id: 'mensajes',
    label: 'Mensajes',
    icon: 'ri-chat-3-line',
    color: 'violet',
    unit: '',
    group: 'community',
    hoy: { value: 562, trend: 'up', trendLabel: '+8.1%' },
    semana: { value: 3890, trend: 'up', trendLabel: '+5.7%' },
    mes: { value: 15700, trend: 'up', trendLabel: '+12.4%' },
    ano: { value: 178200, trend: 'up', trendLabel: '+22.1%' },
  },
  {
    id: 'canciones',
    label: 'Canciones',
    icon: 'ri-music-2-line',
    color: 'emerald',
    unit: '',
    group: 'community',
    hoy: { value: 34, trend: 'up', trendLabel: '+21.4%' },
    semana: { value: 218, trend: 'up', trendLabel: '+13.5%' },
    mes: { value: 890, trend: 'up', trendLabel: '+9.6%' },
    ano: { value: 9400, trend: 'up', trendLabel: '+16.8%' },
  },
  {
    id: 'comentarios',
    label: 'Comentarios',
    icon: 'ri-message-2-line',
    color: 'sky',
    unit: '',
    group: 'community',
    hoy: { value: 143, trend: 'up', trendLabel: '+6.7%' },
    semana: { value: 1020, trend: 'up', trendLabel: '+3.9%' },
    mes: { value: 4100, trend: 'up', trendLabel: '+8.2%' },
    ano: { value: 45200, trend: 'up', trendLabel: '+19.4%' },
  },
  {
    id: 'compartidos',
    label: 'Compartidos',
    icon: 'ri-share-forward-line',
    color: 'amber',
    unit: '',
    group: 'community',
    hoy: { value: 56, trend: 'up', trendLabel: '+27.3%' },
    semana: { value: 387, trend: 'up', trendLabel: '+18.7%' },
    mes: { value: 1620, trend: 'up', trendLabel: '+15.1%' },
    ano: { value: 18150, trend: 'up', trendLabel: '+35.2%' },
  },
];

/* ═══════════════════════════════════════════════════════════
   3. REVENUE KPIs (from adminMonetizationUnified.ts — authoritative)
   ═══════════════════════════════════════════════════════════ */

export const revenueKpis: AnalyticsKpi[] = unifiedRevenueKpis.map((kpi) => ({
  ...kpi,
  group: 'revenue' as const,
}));

/* ═══════════════════════════════════════════════════════════
   4. PERFORMANCE KPIs (from adminMonetizationUnified.ts — authoritative)
   ═══════════════════════════════════════════════════════════ */

export const performanceKpis: AnalyticsKpi[] = unifiedPerformanceKpis.map((kpi) => ({
  ...kpi,
  group: 'performance' as const,
}));

/* ═══════════════════════════════════════════════════════════
   5. ALL KPIs (flat list, group-discriminated)
   ═══════════════════════════════════════════════════════════ */

export const allKpis: AnalyticsKpi[] = [
  ...audienceKpis,
  ...communityKpis,
  ...revenueKpis,
  ...performanceKpis,
];

/* ═══════════════════════════════════════════════════════════
   6. UNIFIED TOPS (single source, no duplicates)
   ═══════════════════════════════════════════════════════════ */

/* ─── Audience Tops ─── */
const audienceProgramasTops: TopItem[] = [
  { rank: 1, name: 'Mañanas con Energía', value: 4250, unit: 'oyentes', trend: 'up', subtitle: 'María Gutiérrez' },
  { rank: 2, name: 'Tardes de Rock', value: 3890, unit: 'oyentes', trend: 'up', subtitle: 'Carlos Méndez' },
  { rank: 3, name: 'Noticiero Central', value: 3420, unit: 'oyentes', trend: 'flat', subtitle: 'Laura Fernández' },
  { rank: 4, name: 'Noches de Jazz', value: 2980, unit: 'oyentes', trend: 'down', subtitle: 'Diego Ramírez' },
  { rank: 5, name: 'Fin de Semana Mix', value: 2610, unit: 'oyentes', trend: 'up', subtitle: 'Ana Torres' },
  { rank: 6, name: 'Deportivo Express', value: 2340, unit: 'oyentes', trend: 'up', subtitle: 'Pablo Castillo' },
  { rank: 7, name: 'Cultura Viva', value: 1980, unit: 'oyentes', trend: 'down', subtitle: 'Sofía Herrera' },
];

const audienceLocutoresTops: TopItem[] = [
  { rank: 1, name: 'Carlos Méndez', value: 15200, unit: 'oyentes', trend: 'up', subtitle: 'Tardes de Rock' },
  { rank: 2, name: 'María Gutiérrez', value: 14100, unit: 'oyentes', trend: 'up', subtitle: 'Mañanas con Energía' },
  { rank: 3, name: 'Laura Fernández', value: 12800, unit: 'oyentes', trend: 'flat', subtitle: 'Noticiero Central' },
  { rank: 4, name: 'Diego Ramírez', value: 10200, unit: 'oyentes', trend: 'down', subtitle: 'Noches de Jazz' },
  { rank: 5, name: 'Ana Torres', value: 8900, unit: 'oyentes', trend: 'up', subtitle: 'Fin de Semana Mix' },
  { rank: 6, name: 'Pablo Castillo', value: 7500, unit: 'oyentes', trend: 'up', subtitle: 'Deportivo Express' },
  { rank: 7, name: 'Sofía Herrera', value: 6200, unit: 'oyentes', trend: 'down', subtitle: 'Cultura Viva' },
];

const audienceEventosTops: TopItem[] = [
  { rank: 1, name: 'Festival de Música Latina', value: 1820, unit: 'asistentes', trend: 'up', subtitle: '15 Jun 2026' },
  { rank: 2, name: 'Encuentro de Locutores 2026', value: 1450, unit: 'asistentes', trend: 'up', subtitle: '22 May 2026' },
  { rank: 3, name: 'Maratón Solidaria 24h', value: 980, unit: 'asistentes', trend: 'flat', subtitle: '08 Abr 2026' },
  { rank: 4, name: 'Noche de Talentos Locales', value: 760, unit: 'asistentes', trend: 'down', subtitle: '30 Mar 2026' },
  { rank: 5, name: 'Taller de Producción Radial', value: 520, unit: 'asistentes', trend: 'up', subtitle: '12 Feb 2026' },
];

const audienceConcursosTops: TopItem[] = [
  { rank: 1, name: 'Adiviná la Canción', value: 2340, unit: 'participantes', trend: 'up', subtitle: 'Mañanas con Energía' },
  { rank: 2, name: 'Trivia Musical del Finde', value: 1890, unit: 'participantes', trend: 'up', subtitle: 'Fin de Semana Mix' },
  { rank: 3, name: 'El Mejor Mensaje', value: 1560, unit: 'participantes', trend: 'flat', subtitle: 'Tardes de Rock' },
  { rank: 4, name: 'Karaoke Radial', value: 1320, unit: 'participantes', trend: 'up', subtitle: 'Noches de Jazz' },
  { rank: 5, name: 'Desafío Deportivo', value: 980, unit: 'participantes', trend: 'down', subtitle: 'Deportivo Express' },
];

/* ─── Community Tops ─── */
const communityMiembrosTops: TopItem[] = [
  { rank: 1, name: 'Camila Torres', value: 2140, unit: 'interacciones', trend: 'up', subtitle: 'Top Fan #1 · VIP Nivel 3' },
  { rank: 2, name: 'Sofía Ramírez', value: 1890, unit: 'interacciones', trend: 'up', subtitle: 'Top Fan #2 · VIP Nivel 3' },
  { rank: 3, name: 'Laura Castillo', value: 1650, unit: 'interacciones', trend: 'flat', subtitle: 'Top Fan #3 · VIP Nivel 2' },
  { rank: 4, name: 'Mariana Quesada', value: 1420, unit: 'interacciones', trend: 'up', subtitle: 'Top Fan #4 · Embajadora Heredia' },
  { rank: 5, name: 'Andrés López', value: 1280, unit: 'interacciones', trend: 'down', subtitle: 'Top Fan #5 · Embajador Cartago' },
  { rank: 6, name: 'Martín Gutiérrez', value: 1040, unit: 'interacciones', trend: 'up', subtitle: 'VIP Nivel 1' },
  { rank: 7, name: 'Carlos Mendoza', value: 890, unit: 'interacciones', trend: 'flat', subtitle: 'Seguidor activo' },
];

const communityProgramasTops: TopItem[] = [
  { rank: 1, name: 'Mañanas con Energía', value: 8450, unit: 'interacciones', trend: 'up', subtitle: 'Valentina Suárez' },
  { rank: 2, name: 'Tardes de Pop', value: 7120, unit: 'interacciones', trend: 'up', subtitle: 'Roberto Díaz' },
  { rank: 3, name: 'Noches de Radio', value: 5680, unit: 'interacciones', trend: 'flat', subtitle: 'Andrés Vega' },
  { rank: 4, name: 'Comunidad Musical CR', value: 4230, unit: 'interacciones', trend: 'down', subtitle: 'Roberto Díaz' },
  { rank: 5, name: 'Escuela de Locutores', value: 3890, unit: 'interacciones', trend: 'up', subtitle: 'Valentina Suárez' },
  { rank: 6, name: 'Radiodeportes Comunidad', value: 3120, unit: 'interacciones', trend: 'up', subtitle: 'Andrés Vega' },
  { rank: 7, name: 'Voces de Nuestra Tierra', value: 2450, unit: 'interacciones', trend: 'down', subtitle: 'Valentina Suárez' },
];

const communityHorariosTops: TopItem[] = [
  { rank: 1, name: 'Lun-Vie 7:00 - 10:00 AM', value: 2480, unit: 'interacciones', trend: 'up', subtitle: 'Mañanas con Energía' },
  { rank: 2, name: 'Lun-Vie 14:00 - 18:00', value: 2120, unit: 'interacciones', trend: 'up', subtitle: 'Tardes de Pop' },
  { rank: 3, name: 'Lun-Vie 21:00 - 00:00', value: 1750, unit: 'interacciones', trend: 'flat', subtitle: 'Noches de Radio' },
  { rank: 4, name: 'Sábados 10:00 AM', value: 1580, unit: 'interacciones', trend: 'up', subtitle: 'Escuela de Locutores' },
  { rank: 5, name: 'Sábados 14:00 - 18:00', value: 1320, unit: 'interacciones', trend: 'down', subtitle: 'Radiodeportes Comunidad' },
];

/* ─── All Tops Unified ─── */
export const unifiedTops: UnifiedTops = {
  // Audience
  programas: audienceProgramasTops,
  locutores: audienceLocutoresTops,
  eventos: audienceEventosTops,
  concursos: audienceConcursosTops,
  // Community
  miembros: communityMiembrosTops,
  comunidadProgramas: communityProgramasTops,
  horarios: communityHorariosTops,
  // Revenue (from unified monetization)
  campanas: unifiedTopsData.campanas,
  patrocinadores: unifiedTopsData.patrocinadores,
  marketplace: unifiedTopsData.marketplace,
};

/* ═══════════════════════════════════════════════════════════
   7. SUMMARY DATA (consistent with unified monetization)
   ═══════════════════════════════════════════════════════════ */

export const analyticsSummary = {
  // Revenue totals (from adminMonetizationUnified.ts authoritative source)
  ownAdsRevenue,
  rfwRadioRevenue,
  sponsorshipsRevenue,
  marketplaceRevenue,
  totalRadioRevenue,
  totalGrossRevenue,
  totalImpressions: unifiedTotalImpressions,
  totalClicks: unifiedTotalClicks,
  avgCtr: unifiedAvgCtr,
  monthLabel,
  // Revenue share
  revenueShareRadio: 70,
  revenueShareRfw: 30,
};

/* ═══════════════════════════════════════════════════════════
   8. HELPER FUNCTIONS
   ═══════════════════════════════════════════════════════════ */

export function getTrendPercentage(current: number, prev: number): string {
  if (prev === 0) return '+100%';
  const diff = ((current - prev) / prev) * 100;
  return `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}%`;
}

export function getStationAnalytics() {
  return {
    kpis: audienceKpis,
    tops: {
      programas: audienceProgramasTops,
      locutores: audienceLocutoresTops,
      eventos: audienceEventosTops,
      concursos: audienceConcursosTops,
    },
  };
}

export function getCommunityAnalytics() {
  return {
    kpis: communityKpis,
    tops: {
      miembros: communityMiembrosTops,
      comunidadProgramas: communityProgramasTops,
      horarios: communityHorariosTops,
    },
  };
}

export function getRevenueAnalytics() {
  return {
    kpis: revenueKpis,
    performanceKpis,
    tops: {
      campanas: unifiedTopsData.campanas,
      patrocinadores: unifiedTopsData.patrocinadores,
      marketplace: unifiedTopsData.marketplace,
    },
  };
}

/* ═══════════════════════════════════════════════════════════
   9. RE-EXPORT TIME PERIOD LABELS
   ═══════════════════════════════════════════════════════════ */

export { monetizationTimePeriodLabels };